import React, { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { api } from '../../services/api';

const DISMISSAL_KEY = 'push_prompt_dismissed_at';
const SUPPRESSION_DAYS = 30;

/**
 * Non-blocking prompt shown on first login after notification feature availability.
 * Guides the coach through enabling browser push notifications.
 *
 * Requirements: 15.1, 15.2, 15.3, 15.4
 */
export const PushPermissionPrompt: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Don't show if browser doesn't support notifications or service workers
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;

    // Already granted or denied at browser level — don't prompt
    if (Notification.permission === 'granted' || Notification.permission === 'denied') return;

    // Check 30-day suppression
    const dismissedAt = localStorage.getItem(DISMISSAL_KEY);
    if (dismissedAt) {
      const elapsed = Date.now() - Number(dismissedAt);
      if (elapsed < SUPPRESSION_DAYS * 24 * 60 * 60 * 1000) return;
    }

    setVisible(true);
  }, []);

  const handleEnable = async () => {
    setLoading(true);
    try {
      const permission = await Notification.requestPermission();

      if (permission === 'granted') {
        // Wait for service worker with timeout
        const swReady = await Promise.race([
          navigator.serviceWorker.ready,
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Service worker not ready after 10s')), 10000),
          ),
        ]);
        const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';
        if (!vapidKey) {
          throw new Error('VAPID public key not configured');
        }
        const subscription = await swReady.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });

        // POST subscription to backend
        const subJson = subscription.toJSON();
        await api.post('/notifications/subscriptions', {
          endpoint: subJson.endpoint,
          keys: {
            p256dh: subJson.keys?.p256dh || '',
            auth: subJson.keys?.auth || '',
          },
        });

        setVisible(false);
      } else {
        // Permission denied
        setDenied(true);
        localStorage.setItem(DISMISSAL_KEY, String(Date.now()));
      }
    } catch (err) {
      console.error('Failed to enable push notifications:', err);
      setDenied(true);
      localStorage.setItem(DISMISSAL_KEY, String(Date.now()));
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSAL_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm w-full px-4 sm:px-0">
      <Card className="bg-white border border-gray-200 shadow-lg rounded-xl">
        <CardContent className="p-4">
          {denied ? (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Bell className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Notifications blocked</p>
                  <p className="text-sm text-gray-500 mt-1">
                    You can enable notifications later in your browser settings.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setVisible(false)}
                className="w-full min-h-[44px]"
              >
                Got it
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-3">
                  <Bell className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Stay on top of your coaching</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Get reminders for upcoming assessments, overdue reviews, and training sessions.
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleDismiss}
                  className="text-gray-400 hover:text-gray-600 p-1 min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Dismiss notification prompt"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <Button
                onClick={handleEnable}
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700 text-white min-h-[44px]"
              >
                {loading ? 'Enabling...' : 'Enable Notifications'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/** Convert URL-safe base64 VAPID key to Uint8Array for pushManager.subscribe */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
